import { fromMarkdown } from 'mdast-util-from-markdown';
import { toMarkdown } from 'mdast-util-to-markdown';
import { gfmFromMarkdown, gfmToMarkdown } from 'mdast-util-gfm';
import { gfm } from 'micromark-extension-gfm';
import type { Root, Nodes } from 'mdast';

export const MARKDOWN_MAX_BYTES = 2 * 1024 * 1024;
export type MarkdownIssue = { code: string; message: string; offset: number | null };
export type MarkdownDocument = {
  source: string;
  tree: Root;
  bom: boolean;
  lineEnding: '\n' | '\r\n';
  trailingNewlines: number;
  issues: readonly MarkdownIssue[];
  canFormat: boolean;
  canConvert: boolean;
};
export class MarkdownInputError extends Error {
  constructor(message: string) { super(message); this.name = 'MarkdownInputError'; }
}
const parseTree = (source: string): Root => fromMarkdown(source, {
  extensions: [gfm()], mdastExtensions: [gfmFromMarkdown()]
});

/** Library-managed URL interpretation must not turn obfuscated schemes into active links. */
export function isSafeMarkdownLink(url: string): boolean {
  let value = url;
  for (let round = 0; round < 3; round++) {
    if (/[\u0000-\u0020\u007f\\]/u.test(value)) return false;
    if (value.startsWith('#')) return true;
    if (value.startsWith('/') && !value.startsWith('//')) return true;
    if (!/^(https?:\/\/|mailto:|tel:)/i.test(value)) return false;
    try {
      const decoded = decodeURIComponent(value);
      if (decoded === value) break;
      value = decoded;
    } catch { return false; }
  }
  try {
    const parsed = new URL(value);
    return ['http:', 'https:'].includes(parsed.protocol) ? Boolean(parsed.hostname) : ['mailto:', 'tel:'].includes(parsed.protocol) && Boolean(parsed.pathname);
  } catch { return false; }
}
export function validateMarkdownText(source: string): void {
  // TextEncoder replaces lone surrogates; reject them before encoding rather than saving replacement bytes.
  for (let i = 0; i < source.length; i++) {
    const unit = source.charCodeAt(i);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = source.charCodeAt(++i);
      if (!(next >= 0xdc00 && next <= 0xdfff)) throw new MarkdownInputError('Markdown contains invalid Unicode.');
    } else if (unit >= 0xdc00 && unit <= 0xdfff) throw new MarkdownInputError('Markdown contains invalid Unicode.');
  }
  if (new TextEncoder().encode(source).byteLength > MARKDOWN_MAX_BYTES) throw new MarkdownInputError('Markdown editing is limited to 2 MiB of UTF-8 text. The original has not been truncated.');
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(source)) throw new MarkdownInputError('Markdown contains non-text control characters.');
}
export function decodeMarkdownBytes(bytes: Uint8Array): string {
  if (bytes.byteLength > MARKDOWN_MAX_BYTES) throw new MarkdownInputError('Markdown editing is limited to 2 MiB of UTF-8 text. The original has not been truncated.');
  let source: string;
  try { source = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes); }
  catch { throw new MarkdownInputError('This file is not valid UTF-8. Download the original and repair its encoding before editing.'); }
  validateMarkdownText(source);
  return source;
}
const supported = new Set(['root', 'paragraph', 'text', 'heading', 'emphasis', 'strong', 'delete', 'list', 'listItem', 'blockquote', 'link', 'inlineCode', 'code', 'thematicBreak', 'break', 'table', 'tableRow', 'tableCell']);
function inspectTree(tree: Root): MarkdownIssue[] {
  const issues: MarkdownIssue[] = [];
  function issue(node: Nodes, code: string, message: string) {
    issues.push({ code, message: message + ' Use Source; formatted editing and conversion are unavailable.', offset: node.position?.start.offset ?? null });
  }
  function visit(node: Nodes, depth: number) {
    if (depth > 64) throw new MarkdownInputError('Markdown nesting exceeds the editing limit.');
    if (!supported.has(node.type)) issue(node, node.type, 'This Markdown contains unsupported ' + node.type + '.');
    if ((node.type === 'link' || node.type === 'definition' || node.type === 'image') && !isSafeMarkdownLink(node.url)) throw new MarkdownInputError('Markdown contains an unsafe link. Use an http, https, mailto, tel, root-relative or anchor link.');
    if (node.type === 'listItem' && node.checked != null) issue(node, 'task-list', 'Task lists must retain their source.');
    if (node.type === 'table' && node.align?.some(value => value != null)) issue(node, 'aligned-table', 'Aligned tables must retain their source.');
    if (node.type === 'code' && node.meta) issue(node, 'code-meta', 'Code fence metadata must retain its source.');
    if (node.type === 'text' && /(?:\$[^$\n]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\])/.test(node.value)) issue(node, 'math', 'Math must retain its source.');
    if ('children' in node) for (const child of node.children) visit(child, depth + 1);
  }
  visit(tree, 0);
  return issues;
}
export function parseMarkdown(source: string): MarkdownDocument {
  validateMarkdownText(source);
  const bom = source.startsWith('\ufeff');
  const body = bom ? source.slice(1) : source;
  const tree = parseTree(body), issues = inspectTree(tree);
  const endings = body.match(/\r\n|\r|\n/g) ?? [];
  const lineEnding = endings.length && endings.every(value => value === '\r\n') ? '\r\n' : '\n';
  if (endings.some(value => value !== lineEnding)) issues.push({ code: 'mixed-endings', message: 'Mixed line endings are preserved in Source. Formatted editing and conversion are unavailable.', offset: null });
  // These extensions can parse as ordinary CommonMark prose/rules; retain their original source.
  // Ignore fenced/indented code by inspecting only non-code source ranges.
  const codeRanges: Array<{ start: number; end: number }> = [];
  function collectCode(nodes: readonly Nodes[]): void {
    for (const node of nodes) {
      if (node.type === 'code' && node.position) codeRanges.push({ start: node.position.start.line, end: node.position.end.line });
      else if ('children' in node) collectCode(node.children);
    }
  }
  collectCode(tree.children);
  let range = 0;
  const nonCodeLines = body.split(/\r\n|\r|\n/).map((line, index) => {
    const number = index + 1;
    while (codeRanges[range] && codeRanges[range]!.end < number) range++;
    return codeRanges[range] && number >= codeRanges[range]!.start ? '' : line;
  });
  if (/^(?:---|\+\+\+)\s*\n/.test(body.replace(/\r\n/g, '\n'))) issues.push({ code: 'frontmatter', message: 'Frontmatter requires Source; conversion and formatted editing are unavailable.', offset: 0 });
  if (nonCodeLines.some(line => /^\s*:{2,}|^\s*\$\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|(?:^|\s):[A-Za-z][\w-]*\[/.test(line))) issues.push({ code: 'extension', message: 'Directives or math require Source; conversion and formatted editing are unavailable.', offset: null });
  const trailing = body.match(/(?:\r\n|\r|\n)+$/)?.[0] ?? '';
  return { source, tree, bom, lineEnding, trailingNewlines: (trailing.match(/\r\n|\r|\n/g) ?? []).length, issues, canFormat: issues.length === 0, canConvert: issues.length === 0 };
}
export const readMarkdown = (bytes: Uint8Array): MarkdownDocument => parseMarkdown(decodeMarkdownBytes(bytes));
function semantic(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(semantic);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'position').map(([key, child]) => [key, semantic(child)]));
  return value;
}
/** A view or equivalent AST is byte-preserving. Only an actual formatted edit serializes. */
export function serializeMarkdown(original: MarkdownDocument, tree: Root): string {
  if (JSON.stringify(semantic(tree)) === JSON.stringify(semantic(original.tree))) return original.source;
  if (!original.canFormat || inspectTree(tree).length) throw new MarkdownInputError('This content requires Source; no formatted change was applied.');
  let body = toMarkdown(tree, { extensions: [gfmToMarkdown()], bullet: '-', emphasis: '*', strong: '*' }).replace(/\n+$/, '');
  body = body.replace(/\n/g, original.lineEnding) + original.lineEnding.repeat(original.trailingNewlines);
  const source = (original.bom ? '\ufeff' : '') + body;
  const result = parseMarkdown(source);
  if (!result.canFormat) throw new MarkdownInputError('The formatted change contains unsupported syntax. Use Source.');
  return source;
}
export function markdownPaste(input: { kind: 'paste' | 'drop'; text?: string; html?: string }): MarkdownDocument {
  if (input.kind === 'drop' || input.html || !input.text) throw new MarkdownInputError('Use Source to insert dropped or HTML content; no lossy change was applied.');
  const parsed = parseMarkdown(input.text);
  if (!parsed.canFormat) throw new MarkdownInputError('This pasted Markdown requires Source; no content was discarded.');
  return parsed;
}
export type { Root as MarkdownRoot, Nodes as MarkdownNode } from 'mdast';
